// @flow

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';
import { type PrsSupport } from '@tectonics/prs/support';
import { type AppValues } from '@tectonics/app-values';
import { type IncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { type LookAndFeel } from '@tectonics/look-and-feel';
import { mapOutgoingPayment, PlaceOrderError, placeOrder, type PlaceOrderErrorPayload } from '@tectonics/prs/outgoing-payment';
import { isVoucher } from '@tectonics/prs/payment-method';
import { type UserInputValidationErrors, type UserInputValidator, filterUserInputValidationErrors } from '@tectonics/prs/validation';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { useSubmitTriesCount, useInputErrors, watchSubmitTimeout } from '@tectonics/abw';
import { GooglePayCanceledError, GooglePayError } from '@tectonics/prs/google-pay';
import { FingerprintError } from '@tectonics/prs/fingerprint';
import { handleWarning, handleError } from '@tectonics/alert-handler';
import { ThreeDSecureError } from '@tectonics/prs/threedsecure';
import { getCurrentPaymentMethodOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';
import {
  getRelatedInputNameFromError,
  getUserErrorInputField,
} from '@tectonics/controller/error-messages/get-input-name-from-error';
import { useControllerContext, useLocalTenancyAsTenant } from './context';
import { mapUserInputValidation } from './map-user-input-validation';
import { isUserInputError, isConcurrencyError } from './error-messages';
import { sendCheckoutFailAnalytics } from '@tectonics/controller/analytics';
import { executeRecaptchaV3 } from '@tectonics/captcha/recaptcha-v3/execute-recaptcha-v3';
import { sendGA4Event, buildCommonEventParams, sendGA4VoucherGeneratedOnce } from '@tectonics/controller/ga4';

type SetUserInputValidationErrors = ((UserInputValidationErrors) => UserInputValidationErrors) => void;

type UseSubmitArgs = {|
  +lookAndFeel: LookAndFeel,
  +spec: PaymentSpec,
  +prsSupport: PrsSupport,
  +incomingPayment: IncomingPayment,
  +setIncomingPayment: (IncomingPayment) => void,
  +setSubmitError: (?PlaceOrderErrorPayload) => void,
  +appValues: AppValues,
  +setUserInputValidationErrors: SetUserInputValidationErrors,
  +validateUserInput: UserInputValidator,
  +userInputValues: UserInputValues,
|};

function sendGA4PaymentFailed(
  commonGA4Params: { [string]: mixed },
  selectedPaymentMethod: string,
  errorType: string,
) {
  sendGA4Event('payment_failed', {
    ...commonGA4Params,
    payment_method: selectedPaymentMethod,
    error_type: errorType,
  });
}

export function useSubmit({
  lookAndFeel,
  spec,
  prsSupport,
  incomingPayment,
  setIncomingPayment,
  setSubmitError,
  appValues,
  setUserInputValidationErrors,
  validateUserInput,
  userInputValues,
}: UseSubmitArgs): [boolean, (boolean, boolean) => Promise<bool>] {
  const registerSubmitTry = useSubmitTriesCount(appValues);
  const registerInputErrors = useInputErrors(appValues);
  const [submitting, setSubmitting] = useState(false);
  const { localTenancy } = useControllerContext();
  const tenant = useLocalTenancyAsTenant();
  const { t } = useTranslation('error-pages');

  const handlePlaceOrderError = (error: PlaceOrderError) => {
    if (isUserInputError(error.payload.code)) {
      const message = t(`errorMessages.${error.payload.code}`, { defaultValue: '' });
      const name = getRelatedInputNameFromError(error.payload.code, getCountry(incomingPayment));

      if (incomingPayment.status === 'OP') {
        const userErrorInputField = getUserErrorInputField(error.payload.code);
        const optionalFields = getCurrentPaymentMethodOptionalFields(incomingPayment, userInputValues, spec);
        if (optionalFields && optionalFields.includes(userErrorInputField)) {
          setSubmitError({
            code: 'CI-000',
            message: t('errorMessages.CI-000', { defaultValue: '' }) || error.payload.message,
          });
          handleError({
            id: 'required-field-set-as-optional',
            payload: {
              message: message || error.payload.message,
              code: error.payload.code,
            },
          });
        }
      }
      setUserInputValidationErrors(results => ({
        ...results,
        [name]: {
          id: 'server-error',
          payload: {
            message: message || error.payload.message,
            code: error.payload.code,
          },
        },
      }));
    } else {
      setSubmitError({
        code: error.payload.code,
        message: t(`errorMessages.${error.payload.code}`, { defaultValue: '' }) || error.payload.message,
      });
    }
  };

  const handlePaymentError = (
    error: mixed,
    commonGA4Params: { [string]: mixed },
    selectedPaymentMethod: string,
  ): boolean => {
    if (error instanceof GooglePayCanceledError) {
      return true;
    }

    if (error instanceof GooglePayError) {
      sendGA4PaymentFailed(commonGA4Params, selectedPaymentMethod, 'google_pay');
      setSubmitError({ code: 'GOOGLE-PAY', message: error.message });
      return false;
    }

    if (error instanceof FingerprintError) {
      sendGA4PaymentFailed(commonGA4Params, selectedPaymentMethod, 'fingerprint');
      handleWarning(error);
      return false;
    }

    if (error instanceof ThreeDSecureError) {
      sendGA4PaymentFailed(commonGA4Params, selectedPaymentMethod, 'three_d_secure');
      setSubmitError({ code: 'THREEDSECURE', message: error.message });
      return false;
    }

    if (!(error instanceof PlaceOrderError)) {
      sendGA4PaymentFailed(commonGA4Params, selectedPaymentMethod, 'unknown');
      handleError(error);
      return false;
    }

    if (isConcurrencyError(error.payload.code)) {
      sendGA4PaymentFailed(commonGA4Params, selectedPaymentMethod, 'concurrency');
      window.location.reload();
      return false;
    }

    sendGA4PaymentFailed(commonGA4Params, selectedPaymentMethod, `place_order_${error.payload.code}`);
    handlePlaceOrderError(error);
    return false;
  };

  const handleSubmit = async (hasValidatedLAF: boolean, hasLAFValidationErrors: boolean): Promise<bool> => {
    setSubmitting(true);
    setSubmitError(null);

    let commonGA4Params = {};
    let selectedPaymentMethod = userInputValues['selectedPaymentMethod'] || '';

    try {
      commonGA4Params = buildCommonEventParams(incomingPayment);
      selectedPaymentMethod = userInputValues['selectedPaymentMethod'] || commonGA4Params.payment_method || '';
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('GA4 buildCommonEventParams error:', e);
    }

    try {
      sendGA4Event('payment_submitted', { ...commonGA4Params, payment_method: selectedPaymentMethod });
      let finalUserInputValues = userInputValues;

      if (incomingPayment.enable_recaptcha_v3_enterprise) {
        sendGA4Event('captcha_triggered', {
          merchant_id: commonGA4Params.merchant_id,
          checkout_country: commonGA4Params.checkout_country,
        });
        const recaptchaV3 = await executeRecaptchaV3('place_order');
        finalUserInputValues = {
          ...userInputValues,
          captchaToken: recaptchaV3.token,
        };
      }

      const userInputValidationResult = mapUserInputValidation({ spec, prsSupport, validateUserInput, userInputValues: finalUserInputValues, incomingPayment });
      const userInputValidationErrors = filterUserInputValidationErrors(userInputValidationResult);
      const hasValidationErrors = userInputValidationErrors.length > 0;

      registerSubmitTry(hasValidationErrors);
      registerInputErrors({
        userInputValidationErrors,
        hasValidatedLAF,
        hasLAFValidationErrors,
      });

      if (hasValidationErrors) {
        const errorFieldNames = userInputValidationErrors.map(([fieldName]) => fieldName);
        sendGA4Event('validation_error', {
          merchant_id: commonGA4Params.merchant_id,
          checkout_country: commonGA4Params.checkout_country,
          payment_method: selectedPaymentMethod,
          error_fields: JSON.stringify(errorFieldNames),
        });

        if (ENV_CONSTANTS.ENABLE_OWN_ANALYTICS) {
          try {
            await sendCheckoutFailAnalytics(incomingPayment,
              lookAndFeel,
              userInputValues,
              userInputValidationResult,
              tenant);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Checkout fail analytics error:', e);
          }
        }

        return false;
      }

      const outgoingPayment = await mapOutgoingPayment(incomingPayment, finalUserInputValues, appValues, localTenancy, lookAndFeel.code);
      return await watchSubmitTimeout(async () => {
        const newIncomingPayment = await placeOrder(outgoingPayment, tenant);
        setIncomingPayment(newIncomingPayment);

        const isPaymentProcessed = newIncomingPayment.status !== 'OP';
        if (isPaymentProcessed && newIncomingPayment.status !== 'CA') {
          sendGA4Event('payment_success', { ...commonGA4Params, payment_method: selectedPaymentMethod });
        }

        const resultPaymentTypeCode = newIncomingPayment.payment_type_code || userInputValues['selectedPaymentMethod'] || '';
        if (newIncomingPayment.status === 'PE' && isVoucher(resultPaymentTypeCode)) {
          sendGA4VoucherGeneratedOnce(newIncomingPayment, {
            payment_method: userInputValues['selectedPaymentMethod'] || resultPaymentTypeCode,
          });
        }

        return isPaymentProcessed;
      });
    } catch(error) {
      return handlePaymentError(error, commonGA4Params, selectedPaymentMethod);
    } finally {
      setSubmitting(false);
    }
  };

  return [submitting, handleSubmit];
}
